/**
 * Read-only reusable Copilot component collections. Documented Dataverse shape:
 * https://learn.microsoft.com/power-apps/developer/data-platform/webapi/reference/botcomponentcollection
 * Agent links are N:N; a component's collection parent is a separate lookup.
 * These reads do not prove runtime availability, publication, or tenant completeness.
 */
import { z } from "zod";
import type { DataverseApi } from "../api/dataverse-api.js";
import { ConfigurationError, ValidationError } from "../utils/errors.js";
import { GUID_PATTERN, sanitizeErrorMessage } from "../utils/security.js";

const MAX_LIMIT = 200;
const DEFAULT_LIMIT = 50;
const AGENT_LINK = "bot_botcomponentcollection";
const COLLECTION_SELECT =
  "botcomponentcollectionid,name,schemaname,statecode,statuscode,ismanaged,createdon,modifiedon,_ownerid_value";
const COMPONENT_SELECT =
  "botcomponentid,name,schemaname,componenttype,statecode,statuscode,ismanaged,modifiedon,_parentbotcomponentcollectionid_value";
const AGENT_SELECT = "botid,name,schemaname,statecode,statuscode,publishedon,modifiedon";
const VISIBILITY_NOTE =
  "Only records visible to the current Dataverse caller in the current environment are returned. " +
  "An empty list does not prove absence in the environment. No YAML, configuration, files, or " +
  "conversation content is returned. Metadata and links do not prove publication or runtime availability.";

const guid = z.string().regex(GUID_PATTERN, "Expected a Dataverse GUID.");
const limit = z.number().int().min(1).max(MAX_LIMIT).default(DEFAULT_LIMIT);

export const listCopilotComponentCollectionsInputSchema = z
  .object({ agentId: guid.optional(), limit })
  .strict();

export const getCopilotComponentCollectionInputSchema = z
  .object({ collectionId: guid, childLimit: limit, linkedAgentLimit: limit })
  .strict();

const limitProperty = {
  type: "integer",
  minimum: 1,
  maximum: MAX_LIMIT,
  default: DEFAULT_LIMIT,
};

export const listCopilotComponentCollectionsTool = {
  name: "list_copilot_component_collections",
  description:
    "Read reusable Copilot component collections visible in the current Dataverse environment. " +
    "Optionally restrict to collections linked to one explicit agent. Returns bounded metadata only, " +
    "with truncation and caller-visibility limits; an empty result is not a tenant-wide absence claim. " +
    "Never authorizes, changes, publishes, or executes a collection or agent.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: {
        type: "string",
        pattern: GUID_PATTERN.source,
        description: "Optional existing agent GUID whose linked collections should be inspected",
      },
      limit: { ...limitProperty, description: "Maximum collection metadata rows returned" },
    },
    additionalProperties: false,
  },
  annotations: { readOnlyHint: true, destructiveHint: false, idempotentHint: true },
};

export const getCopilotComponentCollectionTool = {
  name: "get_copilot_component_collection",
  description:
    "Read one reusable Copilot component collection in the current Dataverse environment, with " +
    "bounded metadata for its child components and linked agents. Verifies collection identity and " +
    "each component's collection parent. Does not return YAML, configuration, files, or transcripts. " +
    "Caller visibility and truncation are explicit; metadata is not proof of runtime availability.",
  inputSchema: {
    type: "object" as const,
    properties: {
      collectionId: {
        type: "string",
        pattern: GUID_PATTERN.source,
        description: "Existing reusable component collection GUID",
      },
      childLimit: {
        ...limitProperty,
        description: "Maximum child component metadata rows returned",
      },
      linkedAgentLimit: {
        ...limitProperty,
        description: "Maximum linked agent metadata rows returned",
      },
    },
    required: ["collectionId"],
    additionalProperties: false,
  },
  annotations: { readOnlyHint: true, destructiveHint: false, idempotentHint: true },
};

type Row = Record<string, unknown>;
type Context = { api: DataverseApi | null };

function requireApi(api: DataverseApi | null): DataverseApi {
  if (!api)
    throw new ConfigurationError(
      "A Dataverse environment is required to read Copilot collections.",
    );
  return api;
}

function checkedRow(value: unknown): Row {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new ValidationError("The collection metadata response contains an invalid record.");
  }
  return value as Row;
}

function checkedGuid(value: unknown, expected?: string): string {
  if (
    typeof value !== "string" ||
    !GUID_PATTERN.test(value) ||
    (expected !== undefined && value.toLowerCase() !== expected.toLowerCase())
  ) {
    throw new ValidationError(
      "The returned collection, agent, or component identity could not be verified.",
    );
  }
  return value.toLowerCase();
}

function optionalGuid(value: unknown): string | undefined {
  return value === undefined || value === null ? undefined : checkedGuid(value);
}

function optionalNumber(value: unknown): number | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "number" || !Number.isSafeInteger(value)) {
    throw new ValidationError(
      "The collection metadata response contains an invalid numeric field.",
    );
  }
  return value;
}

function optionalBoolean(value: unknown): boolean | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "boolean") {
    throw new ValidationError(
      "The collection metadata response contains an invalid Boolean field.",
    );
  }
  return value;
}

function textField(
  row: Row,
  key: string,
  maximum: number,
  truncatedFields: string[],
): string | undefined {
  const value = row[key];
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "string") {
    throw new ValidationError("The collection metadata response contains an invalid text field.");
  }
  // Bound work before redaction. No content fields are selected or copied into output.
  const sanitized = Array.from(
    sanitizeErrorMessage(value.slice(0, maximum + 4096)),
    (character) => {
      const code = character.codePointAt(0) ?? 0;
      return code <= 0x1f ||
        code === 0x7f ||
        (code >= 0x202a && code <= 0x202e) ||
        (code >= 0x2066 && code <= 0x2069)
        ? " "
        : character;
    },
  ).join("");
  if (value.length > maximum || sanitized.length > maximum) truncatedFields.push(key);
  return sanitized.slice(0, maximum);
}

function collectionMetadata(row: Row) {
  const truncatedFields: string[] = [];
  return {
    collectionId: checkedGuid(row.botcomponentcollectionid),
    name: textField(row, "name", 256, truncatedFields),
    schemaName: textField(row, "schemaname", 256, truncatedFields),
    stateCode: optionalNumber(row.statecode),
    statusCode: optionalNumber(row.statuscode),
    isManaged: optionalBoolean(row.ismanaged),
    ownerId: optionalGuid(row._ownerid_value),
    createdOn: textField(row, "createdon", 64, truncatedFields),
    modifiedOn: textField(row, "modifiedon", 64, truncatedFields),
    truncatedFields,
  };
}

function componentMetadata(row: Row, collectionId: string) {
  const parentId = checkedGuid(row._parentbotcomponentcollectionid_value, collectionId);
  const truncatedFields: string[] = [];
  return {
    componentId: checkedGuid(row.botcomponentid),
    parentCollectionId: parentId,
    name: textField(row, "name", 256, truncatedFields),
    schemaName: textField(row, "schemaname", 256, truncatedFields),
    componentType: optionalNumber(row.componenttype),
    stateCode: optionalNumber(row.statecode),
    statusCode: optionalNumber(row.statuscode),
    isManaged: optionalBoolean(row.ismanaged),
    modifiedOn: textField(row, "modifiedon", 64, truncatedFields),
    truncatedFields,
  };
}

function agentMetadata(row: Row) {
  const truncatedFields: string[] = [];
  return {
    agentId: checkedGuid(row.botid),
    name: textField(row, "name", 256, truncatedFields),
    schemaName: textField(row, "schemaname", 256, truncatedFields),
    stateCode: optionalNumber(row.statecode),
    statusCode: optionalNumber(row.statuscode),
    publishedOn: textField(row, "publishedon", 64, truncatedFields),
    modifiedOn: textField(row, "modifiedon", 64, truncatedFields),
    truncatedFields,
  };
}

function boundedPage<T>(
  value: unknown,
  nextLink: unknown,
  maximum: number,
  idField: string,
  map: (row: Row) => T,
) {
  if (!Array.isArray(value) || value.length > MAX_LIMIT + 1) {
    throw new ValidationError(
      "The collection metadata response is missing or exceeds its bounded page size.",
    );
  }
  if (nextLink !== undefined && (typeof nextLink !== "string" || !nextLink.trim())) {
    throw new ValidationError(
      "The collection metadata response contains invalid pagination metadata.",
    );
  }
  const seen = new Set<string>();
  // Validate even the single overflow record before accepting a truncated response.
  const rows = value.map((entry: unknown) => {
    const row = checkedRow(entry);
    const id = checkedGuid(row[idField]);
    if (seen.has(id))
      throw new ValidationError(
        "The collection metadata response contains duplicate record identities.",
      );
    seen.add(id);
    return map(row);
  });
  return {
    items: rows.slice(0, maximum),
    returnedCount: Math.min(rows.length, maximum),
    limit: maximum,
    truncated: rows.length > maximum || nextLink !== undefined,
    totalCountKnown: false,
  };
}

export async function handleListCopilotComponentCollections(
  context: Context,
  input: unknown,
): Promise<string> {
  const parsed = listCopilotComponentCollectionsInputSchema.parse(input);
  const api = requireApi(context.api);
  let value: unknown;
  let nextLink: unknown;
  if (parsed.agentId !== undefined) {
    const agent = checkedRow(
      await api.getRow(
        "bots",
        parsed.agentId,
        "botid",
        `${AGENT_LINK}($select=${COLLECTION_SELECT};$top=${parsed.limit + 1})`,
      ),
    );
    checkedGuid(agent.botid, parsed.agentId);
    value = agent[AGENT_LINK];
    nextLink = agent[`${AGENT_LINK}@odata.nextLink`];
  } else {
    const result = await api.queryRows("botcomponentcollections", {
      select: COLLECTION_SELECT,
      orderBy: "name asc,botcomponentcollectionid asc",
      top: parsed.limit + 1,
    });
    value = result.value;
    nextLink = result.nextLink;
  }
  return JSON.stringify({
    readOnly: true,
    scope: "current-environment-caller-visible",
    agentId: parsed.agentId?.toLowerCase(),
    collections: boundedPage(
      value,
      nextLink,
      parsed.limit,
      "botcomponentcollectionid",
      collectionMetadata,
    ),
    visibilityNote: VISIBILITY_NOTE,
  });
}

export async function handleGetCopilotComponentCollection(
  context: Context,
  input: unknown,
): Promise<string> {
  const parsed = getCopilotComponentCollectionInputSchema.parse(input);
  const api = requireApi(context.api);
  const collection = checkedRow(
    await api.getRow(
      "botcomponentcollections",
      parsed.collectionId,
      COLLECTION_SELECT,
      `${AGENT_LINK}($select=${AGENT_SELECT};$top=${parsed.linkedAgentLimit + 1})`,
    ),
  );
  checkedGuid(collection.botcomponentcollectionid, parsed.collectionId);
  const metadata = collectionMetadata(collection);
  const linkedAgents = boundedPage(
    collection[AGENT_LINK],
    collection[`${AGENT_LINK}@odata.nextLink`],
    parsed.linkedAgentLimit,
    "botid",
    agentMetadata,
  );
  const children = await api.queryRows("botcomponents", {
    select: COMPONENT_SELECT,
    filter: `_parentbotcomponentcollectionid_value eq ${parsed.collectionId}`,
    orderBy: "name asc,botcomponentid asc",
    top: parsed.childLimit + 1,
  });
  return JSON.stringify({
    readOnly: true,
    scope: "current-environment-caller-visible",
    collection: metadata,
    components: boundedPage(
      children.value,
      children.nextLink,
      parsed.childLimit,
      "botcomponentid",
      (row) => componentMetadata(row, parsed.collectionId),
    ),
    linkedAgents,
    visibilityNote: VISIBILITY_NOTE,
  });
}
